"use client";

import { useState } from "react";
import ThemedCapture from "@/components/marketing/ThemedCapture";
import { scoreInk } from "@/lib/scoreCalibration";
import type { LayerKey } from "@/lib/previewCapture";

/* ────────────────────────────────────────────────────────────────────────────
 *  LayerTabs — the five layer views, switched the way the product switches
 *  them.
 *
 *  A carousel of these was removed from /preview once (2026-09-07): it asked
 *  the reader to operate it and never showed the SEQUENCE. The sequence now
 *  lives on the rail (the score → the layers → the dossier); this is one
 *  step of it, and its control mirrors the panel's own LayerNav — a name and
 *  a band dot per layer, no numbers (the captures carry those). The dots'
 *  colours come from the shared score ladder, read off the capture sidecar.
 *
 *  All five captures are in the DOM with `hidden`, so the box never resizes
 *  and a lazy, hidden image is not fetched until its tab is opened.
 * ──────────────────────────────────────────────────────────────────────────── */

export interface LayerView {
  key: LayerKey;
  label: string;
  score: number;
  /** DARK capture path; ThemedCapture derives the -light sibling. */
  src: string;
  alt: string;
}

export default function LayerTabs({ layers, width, height }: { layers: LayerView[]; width: number; height: number }) {
  const [active, setActive] = useState<LayerKey>(layers[0].key);

  return (
    // Phone: the tab strip above the capture. Desktop: a column of tabs
    // beside the tall figure, the way a reader picks a layer in the panel.
    <div className="w-full max-w-[290px] sm:max-w-[330px] lg:max-w-none lg:w-auto flex flex-col lg:flex-row lg:items-start gap-3 lg:gap-5">
      <div role="tablist" aria-label="Layers" aria-orientation="vertical" className="flex flex-wrap lg:flex-col gap-1 lg:pt-2 lg:w-[150px] shrink-0">
        {layers.map((l) => {
          const on = l.key === active;
          return (
            <button
              key={l.key}
              role="tab"
              type="button"
              aria-selected={on}
              onClick={() => setActive(l.key)}
              className={`inline-flex items-center gap-2 rounded-md px-2.5 py-1.5 font-data text-[0.62rem] uppercase tracking-[0.14em] transition-colors border text-left ${
                on ? "bg-surface border-edge-strong text-ink" : "border-transparent text-ink-muted hover:text-ink-2"
              }`}
            >
              <span aria-hidden className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: scoreInk(l.score) }} />
              {l.label}
            </button>
          );
        })}
      </div>
      <figure
        className="rounded-2xl border border-edge bg-surface p-1.5 sm:p-2 shadow-[0_40px_120px_-45px_rgb(0_0_0/0.55)] w-full lg:w-auto lg:h-[68svh] shrink-0"
        style={{ aspectRatio: `${width} / ${height}` }}
      >
        {layers.map((l) => (
          <div key={l.key} hidden={l.key !== active} className="w-full h-full">
            <ThemedCapture
              src={l.src}
              alt={l.alt}
              width={width}
              height={height}
              sizes="420px"
              loading="lazy"
              className="rounded-xl w-full h-full"
            />
          </div>
        ))}
      </figure>
    </div>
  );
}
